import * as React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import {
  Button,
  makeStyles,
  mergeClasses,
  shorthands,
  Toast,
  Toaster,
  ToastTitle,
  useToastController,
  useId,
} from "@fluentui/react-components";
import { ArrowLeft20Regular } from "@fluentui/react-icons";
import { apiUrl } from "../utils/api";
import { BRAND } from "../components/risikoanalyseStep/tokens";
import { sectionColorHex } from "../components/risikoanalyseStep/sectionColor";
import BottomBar from "../components/risikoanalyseStep/BottomBar";
import Sidebar from "../components/risikoanalyseStep/Sidebar";
import TocMenu from "../components/risikoanalyseStep/TocMenu";
import { useTocLayout } from "../components/risikoanalyseStep/useTocLayout";
import type { SectionDef } from "../components/risikoanalyseStep/types";
import { ContactSelector } from "../components/ContactSelector";
import StepHero from "../components/transportContractStep/StepHero";
import FieldRenderer from "../components/transportContractStep/FieldRenderer";
import UebersichtPanel from "../components/transportContractStep/UebersichtPanel";
import { TRANSPORT_VERTRAGSARTEN } from "../components/transportContractStep/sections";
import {
  blockCounts,
  hatDaten,
  visibleFields,
} from "../components/transportContractStep/values";
import type {
  ContactSummary,
  TrValue,
  TrValues,
} from "../components/transportContractStep/types";

/**
 * Transport-Vertragsmaske.
 *
 * Aufbau wie die KFZ- und Immobilien-Vertragsmaske — dieselben Karten,
 * Eingaben und die Leiste unten —, aber mit dem Verzeichnis dauerhaft links
 * statt einer Reiterzeile oben. Das ist hier kein Geschmack, sondern Notwendigkeit:
 * die Felddefinition hat fünf Vertragsarten mit je vier Blöcken, also
 * einundzwanzig Schritte. Eine Reiterzeile dafür wäre nicht mehr lesbar.
 *
 * Die Feldstruktur selbst steht in `transportContractStep/sections.ts` und ist
 * die Excel-Felddefinition, nicht Code: welche Vertragsart welche Felder in
 * welcher Reihenfolge führt, wird dort geändert, nicht hier.
 */

const SIDEBAR_WIDTH = 300;
const BOTTOM_BAR_RESERVE = 96;
const DRAFT_LS_PREFIX = "transport-contract-draft:";
const AUTOSAVE_DEBOUNCE_MS = 1500;

const useStyles = makeStyles({
  shell: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    backgroundColor: BRAND.bg,
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
  },
  body: {
    flexGrow: 1,
    display: "flex",
    overflow: "hidden",
  },
  sidebarDesktop: {
    display: "flex",
    flexDirection: "column",
    flexShrink: 0,
    width: `${SIDEBAR_WIDTH}px`,
    ...shorthands.borderRight("1px", "solid", "rgba(45,20,43,0.1)"),
  },
  main: {
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
    overflowY: "auto",
  },
  inner: {
    width: "100%",
    maxWidth: "1080px",
    marginLeft: "auto",
    marginRight: "auto",
    paddingTop: "20px",
    paddingLeft: "16px",
    paddingRight: "16px",
    paddingBottom: `${BOTTOM_BAR_RESERVE}px`,
    flexGrow: 1,
    "@media (min-width: 768px)": {
      paddingLeft: "32px",
      paddingRight: "32px",
    },
  },
  kopf: {
    display: "flex",
    alignItems: "center",
    columnGap: "10px",
    flexWrap: "wrap",
    marginBottom: "12px",
  },
  kopfTitel: {
    margin: 0,
    fontFamily: BRAND.fontPrimary,
    fontSize: "1.05rem",
    fontWeight: 700,
    color: BRAND.ink,
  },
  kontaktZeile: {
    display: "inline-flex",
    alignItems: "baseline",
    columnGap: "8px",
    flexWrap: "wrap",
  },
  kontaktLabel: {
    fontSize: "0.9rem",
    fontWeight: 700,
    color: BRAND.muted,
  },
  kontaktName: {
    fontSize: "1.05rem",
    fontWeight: 700,
    color: BRAND.ink,
  },
  panel: {
    backgroundColor: BRAND.white,
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("14px"),
    paddingTop: "20px",
    paddingBottom: "20px",
    paddingLeft: "22px",
    paddingRight: "22px",
    display: "flex",
    flexDirection: "column",
    rowGap: "14px",
  },
  abschnittPille: {
    display: "inline-block",
    alignSelf: "flex-start",
    paddingTop: "4px",
    paddingBottom: "4px",
    paddingLeft: "10px",
    paddingRight: "10px",
    fontSize: "0.7rem",
    fontWeight: 700,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    color: BRAND.white,
    ...shorthands.borderRadius("999px"),
  },
  titelZeile: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    columnGap: "16px",
    flexWrap: "wrap",
  },
  titel: {
    margin: 0,
    fontFamily: BRAND.fontAccent,
    fontSize: "clamp(1.5rem, 3.4vw, 2.1rem)",
    fontWeight: 400,
    color: BRAND.ink,
    lineHeight: 1.15,
    flexGrow: 1,
    minWidth: "200px",
  },
  beschreibung: {
    marginTop: "6px",
    marginBottom: 0,
    fontSize: "0.92rem",
    color: BRAND.muted,
  },
  heroBox: {
    flexShrink: 0,
  },
  hinweis: {
    fontSize: "0.85rem",
    color: BRAND.muted,
    backgroundColor: "#faf8fb",
    ...shorthands.border("1px", "dashed", "#e0d9e4"),
    ...shorthands.borderRadius("8px"),
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "12px",
    paddingRight: "12px",
  },
  bottomBarFixed: {
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 15,
  },
  bottomBarFixedNebenSidebar: {
    left: `${SIDEBAR_WIDTH}px`,
  },
  tocMenuZeile: {
    marginBottom: "12px",
  },
});

/**
 * Ein Schritt der Maske. Die Übersicht ist der erste, danach folgt je
 * Vertragsart ein Schritt pro Block A–D — einundzwanzig insgesamt.
 */
type Schritt =
  | { kind: "uebersicht" }
  | { kind: "block"; artIdx: number; blockIdx: number };

const SCHRITTE: Schritt[] = [
  { kind: "uebersicht" },
  ...TRANSPORT_VERTRAGSARTEN.flatMap((art, artIdx) =>
    art.blocks.map((_, blockIdx) => ({ kind: "block" as const, artIdx, blockIdx })),
  ),
];

/** Verzeichnis links: Übersicht als eigener Abschnitt, dann die Vertragsarten. */
const TOC_SECTIONS: SectionDef[] = [
  { key: "uebersicht", title: "Vertragsübersicht", color: "orange", subSteps: [] },
  ...TRANSPORT_VERTRAGSARTEN.map((art) => ({
    key: art.key,
    title: art.title,
    color: art.color,
    subSteps: art.blocks.map((b) => ({
      key: b.key,
      title: `${b.letter}. ${b.title}`,
      questions: [],
    })),
  })),
];

/** Schritt-Index ↔ Position im Verzeichnis. */
const zuVerzeichnis = (i: number): { sectionIdx: number; subStepIdx: number } => {
  const s = SCHRITTE[i];
  if (!s || s.kind === "uebersicht") return { sectionIdx: 0, subStepIdx: -1 };
  return { sectionIdx: s.artIdx + 1, subStepIdx: s.blockIdx };
};

const ausVerzeichnis = (sectionIdx: number, subStepIdx: number): number => {
  if (sectionIdx === 0) return 0;
  const artIdx = sectionIdx - 1;
  const blockIdx = Math.max(0, subStepIdx);
  return SCHRITTE.findIndex(
    (s) => s.kind === "block" && s.artIdx === artIdx && s.blockIdx === blockIdx,
  );
};

const TransportContractCreatePage: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const toasterId = useId("transport-contract-toaster");
  const { dispatchToast } = useToastController(toasterId);

  const shellRef = useRef<HTMLDivElement | null>(null);
  const mainRef = useRef<HTMLElement | null>(null);
  const { layout: tocLayout, availableHeight } = useTocLayout(shellRef);

  const routerState = (location.state ?? {}) as { kontaktId?: number };
  const kontaktIdAusQuery = Number(searchParams.get("kontaktId"));
  const vorgewaehlteKontaktId =
    routerState.kontaktId ?? (Number.isFinite(kontaktIdAusQuery) && kontaktIdAusQuery > 0
      ? kontaktIdAusQuery
      : null);

  const [contact, setContact] = useState<ContactSummary | null>(null);
  const [values, setValues] = useState<TrValues>({});
  const [schrittIdx, setSchrittIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState<string | null>(null);

  const draftKey = `${DRAFT_LS_PREFIX}${contact?.id ?? "neu"}`;

  // ── Kontakt vorbelegen, wenn die Maske aus einer Kontaktseite kommt ──────
  useEffect(() => {
    if (!vorgewaehlteKontaktId) return;
    let abgebrochen = false;
    (async () => {
      try {
        const res = await fetch(apiUrl(`contact/${vorgewaehlteKontaktId}`));
        if (!res.ok) return;
        const data = await res.json();
        if (abgebrochen) return;
        setContact({
          id: data.id,
          displayName:
            data.displayName ??
            [data.firstName, data.lastName].filter(Boolean).join(" ") ??
            `Kontakt ${data.id}`,
          kind: data.kind,
        });
      } catch {
        // Ohne vorbelegten Kontakt bleibt die Auswahl auf der Übersicht stehen.
      }
    })();
    return () => {
      abgebrochen = true;
    };
  }, [vorgewaehlteKontaktId]);

  // ── Entwurf laden ───────────────────────────────────────────────────────
  const draftGeladen = useRef(false);
  useEffect(() => {
    if (draftGeladen.current) return;
    try {
      const roh = window.localStorage.getItem(draftKey);
      if (roh) {
        const geparst = JSON.parse(roh) as { values?: TrValues; savedAt?: string };
        if (geparst.values) setValues(geparst.values);
        if (geparst.savedAt) setDraftSavedAt(geparst.savedAt);
      }
    } catch {
      // Ein unlesbarer Entwurf darf die Maske nicht blockieren.
    }
    draftGeladen.current = true;
  }, [draftKey]);

  // ── Entwurf sichern ─────────────────────────────────────────────────────
  const entwurfSchreiben = React.useCallback(() => {
    try {
      const savedAt = new Date().toISOString();
      window.localStorage.setItem(draftKey, JSON.stringify({ values, savedAt }));
      setDraftSavedAt(savedAt);
    } catch {
      // Voller oder gesperrter Speicher — kein Grund, die Eingabe zu stören.
    }
  }, [draftKey, values]);

  useEffect(() => {
    if (!draftGeladen.current) return;
    const t = window.setTimeout(entwurfSchreiben, AUTOSAVE_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [values, entwurfSchreiben]);

  const entwurfJetztSichern = () => {
    setSavingDraft(true);
    entwurfSchreiben();
    window.setTimeout(() => setSavingDraft(false), 300);
  };

  // ── Navigation ──────────────────────────────────────────────────────────
  const schritt = SCHRITTE[schrittIdx] ?? SCHRITTE[0];
  const { sectionIdx, subStepIdx } = zuVerzeichnis(schrittIdx);
  const istLetzter = schrittIdx === SCHRITTE.length - 1;

  const springe = (ziel: number) => {
    if (ziel < 0 || ziel >= SCHRITTE.length) return;
    setSchrittIdx(ziel);
    mainRef.current?.scrollTo({ top: 0, behavior: "auto" });
  };

  const setzeWert = (key: string, value: TrValue | undefined) => {
    setValues((prev) => {
      const naechste = { ...prev };
      const leer =
        value === undefined ||
        value === "" ||
        (Array.isArray(value) && value.length === 0);
      if (leer) delete naechste[key];
      else naechste[key] = value;
      return naechste;
    });
  };

  // ── Fortschritt über alle Eingabefelder ─────────────────────────────────
  const fortschritt = useMemo(() => {
    let filled = 0;
    let total = 0;
    for (const art of TRANSPORT_VERTRAGSARTEN) {
      for (const block of art.blocks) {
        const c = blockCounts(block, values);
        filled += c.filled;
        total += c.total;
      }
    }
    return { filled, total, fraction: total > 0 ? filled / total : 0 };
  }, [values]);

  // ── Absenden ────────────────────────────────────────────────────────────
  const merkmaleBauen = () => {
    const characteristics: Array<{
      nameBezeichnung: string;
      contractCharacteristicValue: string;
    }> = [];
    const push = (name: string, wert: string) => {
      if (wert.trim() === "") return;
      characteristics.push({ nameBezeichnung: name, contractCharacteristicValue: wert });
    };
    for (const art of TRANSPORT_VERTRAGSARTEN) {
      for (const block of art.blocks) {
        for (const field of block.fields) {
          // Anzeigefelder stammen aus der Analyse und werden dort geführt —
          // sie hier mitzuschreiben erzeugte eine zweite, veraltende Kopie.
          if (field.type === "readout") continue;
          const wert = values[field.key];
          if (wert === undefined) continue;
          push(
            `tr.${field.key}`,
            typeof wert === "string" ? wert : JSON.stringify(wert),
          );
        }
      }
    }
    // Welche Vertragsarten überhaupt Angaben tragen — erspart beim Lesen das
    // Durchsuchen aller Merkmale.
    const belegte = TRANSPORT_VERTRAGSARTEN.filter((a) => hatDaten(a, values)).map(
      (a) => a.key,
    );
    if (belegte.length) push("tr.vertragsarten", belegte.join(","));
    return characteristics;
  };

  const absenden = async () => {
    if (!contact) {
      dispatchToast(
        <Toast>
          <ToastTitle>Bitte zuerst den Versicherungsnehmer wählen</ToastTitle>
        </Toast>,
        { intent: "warning" },
      );
      springe(0);
      return;
    }
    const characteristics = merkmaleBauen();
    if (characteristics.length === 0) {
      dispatchToast(
        <Toast>
          <ToastTitle>Noch keine Vertragsangaben erfasst</ToastTitle>
        </Toast>,
        { intent: "warning" },
      );
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        contractNbr: `TR-${Date.now()}`,
        branch: "Transport",
        contact: { id: contact.id },
        currency: "EUR",
        characteristics,
      };
      const res = await fetch(apiUrl("contracts"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const text = await res.text();
        console.error("Contract POST failed", res.status, text);
        let detail = "";
        try {
          const geparst = JSON.parse(text);
          const errors = geparst?.message?.errors ?? geparst?.errors;
          if (errors && typeof errors === "object") {
            detail = Object.values(errors).filter(Boolean).join(" · ");
          }
          if (!detail && typeof geparst?.message === "string") detail = geparst.message;
        } catch {
          if (text) detail = text.slice(0, 200);
        }
        dispatchToast(
          <Toast>
            <ToastTitle>Speichern fehlgeschlagen{detail ? ` — ${detail}` : ""}</ToastTitle>
          </Toast>,
          { intent: "error" },
        );
        return;
      }
      try {
        window.localStorage.removeItem(draftKey);
      } catch {
        // Der Vertrag ist gespeichert — ein liegengebliebener Entwurf ist kein Fehler.
      }
      dispatchToast(
        <Toast>
          <ToastTitle>Transport-Vertrag angelegt</ToastTitle>
        </Toast>,
        { intent: "success" },
      );
      navigate(`/kontakte/${contact.id}`);
    } catch (e) {
      console.error(e);
      dispatchToast(
        <Toast>
          <ToastTitle>Speichern fehlgeschlagen</ToastTitle>
        </Toast>,
        { intent: "error" },
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ── Inhalt des aktuellen Schritts ───────────────────────────────────────
  const inhalt = () => {
    if (schritt.kind === "uebersicht") {
      return (
        <div className={styles.panel}>
          <span
            className={styles.abschnittPille}
            style={{ backgroundColor: sectionColorHex.orange }}
          >
            Vertragsübersicht
          </span>
          <div className={styles.titelZeile}>
            <div style={{ flexGrow: 1, minWidth: 200 }}>
              <h2 className={styles.titel}>Transport-Verträge im Überblick</h2>
              <p className={styles.beschreibung}>
                Eine Vertragsart auswählen. Innerhalb jeder Art stehen zuerst die
                wichtigsten Vertragsdaten, danach die Deckungsdetails, dann
                Bedingungen und Klauseln — und zuletzt die Informationen aus der
                Analyse.
              </p>
            </div>
            <div className={styles.heroBox}>
              <StepHero kind="uebersicht" />
            </div>
          </div>
          {!contact && (
            <ContactSelector
              label="Versicherungsnehmer"
              required
              selectedContact={contact}
              onSelect={(c: any) =>
                setContact(
                  c
                    ? {
                        id: c.id,
                        displayName:
                          c.displayName ??
                          [c.firstName, c.lastName].filter(Boolean).join(" ") ??
                          `Kontakt ${c.id}`,
                        kind: c.kind,
                      }
                    : null,
                )
              }
            />
          )}
          <UebersichtPanel
            values={values}
            onOpen={(artKey) => {
              const artIdx = TRANSPORT_VERTRAGSARTEN.findIndex((a) => a.key === artKey);
              if (artIdx >= 0) springe(ausVerzeichnis(artIdx + 1, 0));
            }}
          />
        </div>
      );
    }

    const art = TRANSPORT_VERTRAGSARTEN[schritt.artIdx];
    const block = art.blocks[schritt.blockIdx];
    const felder = visibleFields(block, values);
    const zaehler = blockCounts(block, values);
    const akzent = sectionColorHex[art.color];

    return (
      <div className={styles.panel}>
        <span className={styles.abschnittPille} style={{ backgroundColor: akzent }}>
          {art.shortTitle}
        </span>
        <div className={styles.titelZeile}>
          <div style={{ flexGrow: 1, minWidth: 200 }}>
            <h2 className={styles.titel}>
              {block.letter}. {block.title}
            </h2>
            <p className={styles.beschreibung}>
              {block.description ?? art.intro}
            </p>
          </div>
          <div className={styles.heroBox}>
            <StepHero kind={art.key} />
          </div>
        </div>
        {block.letter === "D" && (
          <div className={styles.hinweis}>
            Diese Werte stammen aus der Transport-Analyse des Kontakts und werden
            hier nur angezeigt. Geändert werden sie in der Analyse.
          </div>
        )}
        {zaehler.total > 0 && (
          <div style={{ fontSize: "0.82rem", color: BRAND.muted }}>
            {zaehler.filled} von {zaehler.total} Feldern ausgefüllt
          </div>
        )}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
            gap: 14,
          }}
        >
          {felder.map((field) => (
            <FieldRenderer
              key={field.key}
              field={field}
              values={values}
              onChange={setzeWert}
            />
          ))}
        </div>
      </div>
    );
  };

  const aktuelleArt =
    schritt.kind === "block" ? TRANSPORT_VERTRAGSARTEN[schritt.artIdx] : null;
  const fortschrittsText = aktuelleArt
    ? `Schritt ${schrittIdx + 1} von ${SCHRITTE.length} · ${aktuelleArt.shortTitle}`
    : `Schritt ${schrittIdx + 1} von ${SCHRITTE.length} · Übersicht`;

  return (
    <div className={styles.shell} ref={shellRef}>
      <Toaster toasterId={toasterId} />
      <div className={styles.body}>
        {tocLayout === "sidebar" && (
          <aside className={styles.sidebarDesktop}>
            <Sidebar
              sections={TOC_SECTIONS}
              currentSectionIdx={sectionIdx}
              currentSubStepIdx={subStepIdx}
              onSelect={(sIdx, ssIdx) => springe(ausVerzeichnis(sIdx, ssIdx))}
              onSectionSelect={(sIdx) => springe(ausVerzeichnis(sIdx, 0))}
              progressFraction={fortschritt.fraction}
              progressDetail={`${fortschritt.filled}/${fortschritt.total}`}
              eyebrow="Vertragsmaske"
              heading="Transport"
              ariaLabel="Abschnitte der Transport-Vertragsmaske"
              maxHeight={availableHeight}
              bottomInset={BOTTOM_BAR_RESERVE}
            />
          </aside>
        )}

        <main className={styles.main} ref={mainRef}>
          <div className={styles.inner}>
            <div className={styles.kopf}>
              <Button
                appearance="subtle"
                icon={<ArrowLeft20Regular />}
                onClick={() => navigate(-1)}
              >
                Zurück
              </Button>
              <h1 className={styles.kopfTitel}>Vertrag anlegen · Transport</h1>
              {contact && (
                <span className={styles.kontaktZeile}>
                  <span className={styles.kontaktLabel}>Kontakt:</span>
                  <span className={styles.kontaktName}>{contact.displayName}</span>
                </span>
              )}
            </div>

            {tocLayout === "menu" && (
              <div className={styles.tocMenuZeile}>
                <TocMenu
                  sections={TOC_SECTIONS}
                  currentSectionIdx={sectionIdx}
                  currentSubStepIdx={subStepIdx}
                  onSelect={(sIdx, ssIdx) => springe(ausVerzeichnis(sIdx, ssIdx))}
                  onSectionSelect={(sIdx) => springe(ausVerzeichnis(sIdx, 0))}
                  progressFraction={fortschritt.fraction}
                  progressDetail={`${fortschritt.filled}/${fortschritt.total}`}
                  eyebrow="Vertragsmaske"
                  heading="Transport"
                  kontextText={fortschrittsText}
                  maxHeight={availableHeight}
                />
              </div>
            )}

            {inhalt()}
          </div>
        </main>
      </div>

      <div
        className={mergeClasses(
          styles.bottomBarFixed,
          tocLayout === "sidebar" && styles.bottomBarFixedNebenSidebar,
        )}
      >
        <BottomBar
          onPrev={() => springe(schrittIdx - 1)}
          onNext={() => springe(schrittIdx + 1)}
          onSubmit={absenden}
          onSaveDraft={entwurfJetztSichern}
          canPrev={schrittIdx > 0}
          isLastStep={istLetzter}
          submitting={submitting}
          savingDraft={savingDraft}
          draftSavedAt={draftSavedAt}
          progressLabel={fortschrittsText}
          progressFraction={fortschritt.fraction}
          submitLabel="Vertrag anlegen"
          submittingLabel="Wird angelegt…"
        />
      </div>
    </div>
  );
};

export default TransportContractCreatePage;
